import { useState } from 'react';
import { api } from '../lib/api';
import type { Page } from '../lib/api';
import type { AuditView } from '../lib/types';
import { useDebounced, useResource } from '../lib/useResource';
import { Card, Empty, Loading, Notice, Pill, exactly } from '../components/ui';

export function AuditTrail() {
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const debouncedAction = useDebounced(action);

  const events = useResource(
    () =>
      api.get<Page<AuditView>>('/api/v1/audit', {
        action: debouncedAction || undefined,
        page,
        size: 50,
      }),
    [debouncedAction, page],
  );

  return (
    <>
      <div className="page-head">
        <h1>Audit trail</h1>
        <p>
          Every privileged action, successful or refused. The table is append-only in the database:
          the application account cannot alter or delete a row, so history cannot be rewritten from
          inside the platform.
        </p>
      </div>

      <Card>
        <div className="filters">
          <label className="field grow">
            <span>
              Action
              <span className="hint">
                Exact match, for example command.issued, alert.released_to_hr, auth.login
              </span>
            </span>
            <input
              type="search"
              value={action}
              onChange={(event) => { setAction(event.target.value); setPage(0); }}
            />
          </label>
        </div>

        {events.error && <Notice kind="error">{events.error}</Notice>}
        {events.loading && !events.data && <Loading />}
        {events.data?.content.length === 0 && <Empty>No audit entries match.</Empty>}

        {events.data && events.data.content.length > 0 && (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>When</th><th>Outcome</th><th>Action</th><th>Actor</th>
                    <th>Target</th><th>Source</th><th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {events.data.content.map((event) => (
                    <tr key={event.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>{exactly(event.occurredAt)}</td>
                      <td>
                        <Pill tone={event.outcome === 'SUCCESS' ? 'ok' : event.outcome === 'DENIED' ? 'warn' : 'stop'}>
                          {event.outcome.toLowerCase()}
                        </Pill>
                      </td>
                      <td className="mono">{event.action}</td>
                      <td style={{ fontSize: 12.5 }}>
                        {event.actorLabel}
                        <div style={{ color: 'var(--faint)' }}>{event.actorType.toLowerCase()}</div>
                      </td>
                      <td style={{ fontSize: 12.5 }}>
                        {event.targetType ?? '—'}
                        <div className="mono" style={{ color: 'var(--faint)' }}>
                          {event.targetId?.slice(0, 18) ?? ''}
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 12.5 }}>{event.ipAddress ?? '—'}</td>
                      <td style={{ maxWidth: '34ch' }}>
                        <pre className="evidence" style={{ maxHeight: 90, fontSize: 11.5, padding: '7px 9px' }}>
                          {JSON.stringify(event.detail)}
                        </pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pager">
              <span>{events.data.totalElements} entries · page {events.data.page + 1} of {Math.max(1, events.data.totalPages)}</span>
              <button type="button" className="secondary small" disabled={page === 0} onClick={() => setPage((v) => v - 1)}>
                Previous
              </button>
              <button
                type="button"
                className="secondary small"
                disabled={page + 1 >= events.data.totalPages}
                onClick={() => setPage((v) => v + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
